// sidebar.jsx
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { createPortal } from "react-dom";
import logo from "@/assets/logo.png";
import {
  Home,
  Building2,
  Users,
  LogOut,
  Inbox,
  CalendarClock,
} from "lucide-react";

function SideBar({ currentPage, onNavigate }) {
  const [showLogout, setShowLogout] = useState(false);

  const queryClient = useQueryClient();
  const API_URL = import.meta.env.VITE_API_URL;

  const fetchJSON = async (url) => {
    const token = localStorage.getItem("accessToken");
    if (!token) return null;

    const res = await fetch(url, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    });

    if (!res.ok) return null;

    try {
      return await res.json();
    } catch {
      return null;
    }
  };

  const prefetchById = (id) => {
    if (!API_URL) return;

    try {
      const isWarmOrFetching = (key) => {
        const s = queryClient.getQueryState(key);
        return s?.status === "success" || s?.fetchStatus === "fetching";
      };

      // ✅ ADD THIS: dashboard prefetch
      if (id === "dashboard") {
        if (isWarmOrFetching(["dashboard-summary"])) return;

        queryClient.prefetchQuery({
          queryKey: ["dashboard-summary"],
          queryFn: async () => {
            const data = await fetchJSON(
              `${API_URL}/api/sdaowner/dashboard_summary`
            );
            return data || null;
          },
          staleTime: 60_000,
        });

        return;
      }

      if (id === "accommodations") {
        if (isWarmOrFetching(["sdaowner-accommodations"])) return;

        queryClient.prefetchQuery({
          queryKey: ["sdaowner-accommodations"],
          queryFn: async () => {
            const data = await fetchJSON(
              `${API_URL}/api/sdaowner/get_accommodations`
            );

            const baseUrl = (API_URL || "http://localhost:5000").replace(
              /\/$/,
              ""
            );

            const normalized = ((data && data.accommodations) || []).map(
              (acc) => {
                const normalizedImages = (acc.images || [])
                  .map((img) => {
                    if (!img) return null;

                    if (typeof img === "string") {
                      if (/^https?:\/\//.test(img)) return img;
                      const path = img.startsWith("/") ? img : `/${img}`;
                      return `${baseUrl}${path}`;
                    }

                    const candidate =
                      img.url ||
                      img.path ||
                      img.image_url ||
                      img.imageUrl ||
                      "";

                    if (!candidate) return null;
                    if (/^https?:\/\//.test(candidate)) return candidate;

                    const path = candidate.startsWith("/")
                      ? candidate
                      : `/${candidate}`;
                    return `${baseUrl}${path}`;
                  })
                  .filter(Boolean);

                return { ...acc, images: normalizedImages };
              }
            );

            return normalized;
          },
          staleTime: 60_000,
        });

        return;
      }

      if (id === "inquiry") {
        if (isWarmOrFetching(["inquiries"])) return;

        queryClient.prefetchQuery({
          queryKey: ["inquiries"],
          queryFn: async () => {
            const data = await fetchJSON(`${API_URL}/api/sdaowner/inquiries`);
            return (data && (data.inquiries || data)) || [];
          },
          staleTime: 60_000,
        });

        return;
      }

      if (id === "availability") {
        // Availability page uses accommodations, so prefetch the same data
        if (isWarmOrFetching(["sdaowner-accommodations"])) return;

        queryClient.prefetchQuery({
          queryKey: ["sdaowner-accommodations"],
          queryFn: async () => {
            const data = await fetchJSON(
              `${API_URL}/api/sdaowner/get_accommodations`
            );
            return (data && data.accommodations) || [];
          },
          staleTime: 60_000,
        });

        return;
      }

      if (id === "booking") {
        if (isWarmOrFetching(["bookings"])) return;

        queryClient.prefetchQuery({
          queryKey: ["bookings"],
          queryFn: async () => {
            const data = await fetchJSON(
              `${API_URL}/api/sdaowner/get_bookings`
            );
            return (data && (data.bookings || data)) || [];
          },
          staleTime: 60_000,
        });

        return;
      }
    } catch (e) {
      console.error("Sidebar prefetch failed:", e);
    }
  };

  const navItems = [
    { id: "dashboard", label: "Dashboard", icon: Home }, // ✅ now will prefetch dashboard_summary on hover
    { id: "accommodations", label: "Manage Accommodations", icon: Building2 },
    { id: "inquiry", label: "Inquiry", icon: Inbox },
    { id: "availability", label: "Availability", icon: Users },
    { id: "booking", label: "Booking", icon: CalendarClock },
  ];

  const handleLogout = () => {
    // clear auth (must match ProtectedRoute keys)
    localStorage.removeItem("accessToken");
    localStorage.removeItem("refreshToken");
    localStorage.removeItem("user");
    localStorage.removeItem("role");
    localStorage.removeItem("username");

    sessionStorage.removeItem("accessToken");
    sessionStorage.removeItem("refreshToken");
    sessionStorage.removeItem("user");
    sessionStorage.removeItem("role");
    sessionStorage.removeItem("username");

    setShowLogout(false);

    // hard redirect so browser back cache won't show dashboard
    window.location.replace("/login");
  };

  return (
    <>
      <aside className="left-0 top-0 h-full flex flex-col bg-white border-r border-gray-200 z-40">
        {/* Logo */}
        <div className="h-16 flex items-center justify-between px-6 border-b border-gray-200">
          <img src={logo} alt="Logo" className="h-16 w-full" />
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-2">
          {navItems.map(({ id, label, icon: Icon }) => {
            const isActive = id === currentPage;
            return (
              <button
                key={id}
                onMouseEnter={() => prefetchById(id)}
                onFocus={() => prefetchById(id)}
                onClick={() => onNavigate(id)}
                className={`w-full flex items-center gap-3 rounded-lg px-4 py-3 transition-colors ${
                  isActive
                    ? "bg-[#D2138C] text-white"
                    : "text-gray-700 hover:bg-gray-100"
                }`}>
                <Icon className="h-5 w-5" />
                <span>{label}</span>
              </button>
            );
          })}
        </nav>

        {/* Logout */}
        <div className="border-t border-gray-200 px-4 py-3">
          <button
            onClick={() => setShowLogout(true)}
            className="w-full flex items-center gap-3 rounded-lg px-4 py-3 text-gray-700 hover:bg-rose-50 hover:text-rose-600 transition-colors">
            <LogOut className="h-5 w-5" />
            <span>Logout</span>
          </button>
        </div>
      </aside>

      {/* Logout modal */}
      {showLogout &&
        createPortal(
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm"
            aria-modal="true"
            role="dialog">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-sm mx-4">
              <div className="px-6 pt-6">
                <h3 className="text-lg font-semibold text-gray-900">
                  Sign out?
                </h3>
                <p className="mt-2 text-sm text-gray-600">
                  Are you sure you want to sign out? You will need to login
                  again to access the admin dashboard.
                </p>
              </div>
              <div className="flex justify-end gap-3 px-6 py-5 bg-gray-50 rounded-b-xl">
                <button
                  onClick={() => setShowLogout(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-100">
                  Cancel
                </button>
                <button
                  onClick={handleLogout}
                  className="px-4 py-2 text-sm font-semibold text-white bg-[#D2138C] rounded-lg hover:bg-[#b01074]">
                  Sign out
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}
    </>
  );
}

export default SideBar;
